import React, { useState } from "react";
import { Box, Button, Stack } from "@mui/material";
import { AutoComplete } from "./AutoComplete";
import { useUserFilterActions } from "../../hooks/useUserFilterActions";
import { FilterItem, useFilterContext } from "../context/FilterContext";
import { FieldControl } from "./FieldControl";
import { AsyncMultiAutoComplete } from "./AsyncMultiAutoComplete";
import { useInfiniteSites } from "../../features/projects/hooks/useSites";
import { useInfiniteStakeholders } from "../../features/projects/hooks/useStakeholders";

interface FilterSideBarProps {
  hiddenFilters?: Array<
    | "region"
    | "workstream"
    | "programme"
    | "site"
    | "worktype"
    | "worktype-group"
    | "projectYear"
    | "stakeholder"
  >;
  onClose?: () => void;
}

export const FilterSideBar: React.FC<FilterSideBarProps> = ({
  hiddenFilters = [],
  onClose,
}) => {
  const {
    tempFilters,
    options,
    setOptions,
    handleUpdateTempFilters,
    handleUpdateMultipleTempFilters,
    handleApplyFilters,
    handleResetFilters,
  } = useFilterContext();
  const { userFilterActions, getFilteredData, cleanFilters } =
    useUserFilterActions();

  const [siteSearch, setSiteSearch] = useState("");
  const [stakeholderSearch, setStakeholderSearch] = useState("");

  const normalizedRegion = tempFilters.region?.length
    ? tempFilters.region.map(r => Number(r.value))
    : null;

  // Load sites with no region filter (null) and no third-party flag
  const {
    sites,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isSitesLoading,
  } = useInfiniteSites(siteSearch, normalizedRegion, null);

  const {
    stakeholders,
    fetchNextPage: fetchNextStakeholdersPage,
    hasNextPage: hasStakeholdersNextPage,
    isFetchingNextPage: isFetchingStakeholdersNextPage,
    isLoading: isStakeholdersLoading,
  } = useInfiniteStakeholders({
    search: stakeholderSearch,
    region:
      tempFilters.region.length > 0
        ? tempFilters.region?.map((r) => r.value)
        : null,
  });

  const safeArray = (arr: FilterItem[]) => arr.map((v) => Number(v.value));

  const { regions, workstreams } = getFilteredData({
    regionIds: safeArray(tempFilters.region),
  });

  const handleChange = (
    key: "region" | "workstream" | "worktype-group",
    value: FilterItem[],
  ) => {
    const updated = { ...tempFilters, [key]: value };
    const cleaned = cleanFilters(updated, key, userFilterActions);

    handleUpdateMultipleTempFilters({
      ...cleaned,
      ...(key === "region" ? { site: [], stakeholder: [] } : {}),
    });
  };

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i <= currentYear + 14; i++) {
      const yearLabel = `FY${i.toString().slice(-2)}`;
      years.push({ value: yearLabel, label: yearLabel });
    }
    return years;
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        flexGrow={1}
        sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}
      >
        {!hiddenFilters.includes("region") && (
          <FieldControl
            keyName={"region"}
            label={"Network"}
            variant={"select"}
            options={[...regions]
              .sort((a: any, b: any) =>
                (a.name ?? "").localeCompare(b.name ?? ""),
              )
              .map((r: any) => ({
                value: String(r.id),
                label: r.name,
              }))}
            value={tempFilters.region.map(r => r.value)}
            onChange={(_key: string, value: string[]) => {
              const items = regions
                .filter((r: any) => value.includes(String(r.id)))
                .map((r: any) => ({ value: String(r.id), label: r.name }));
              handleChange("region", items);
            }}
            multiSelect
          />
        )}

        {!hiddenFilters?.includes("programme") && (
          <FieldControl
            keyName={"programme"}
            label={"Programme"}
            variant={"select"}
            options={options.programme
              ?.slice()
              .sort((a: any, b: any) =>
                (a.description ?? "").localeCompare(b.description ?? ""),
              )
              .map((r: any) => ({
                value: String(r.id),
                label: r.description,
              }))}
            value={tempFilters.programme.map(p => p.value)}
            onChange={(_key: string, value: string[]) => {
              const items = options.programme
                .filter((r: any) => value.includes(String(r.id)))
                .map((r: any) => ({
                  value: String(r.id),
                  label: r.description,
                }));
              handleUpdateTempFilters("programme", items);
            }}
            multiSelect
          />
        )}

        {!hiddenFilters.includes("workstream") && (
          <FieldControl
            keyName={"workstream"}
            label={"Workstream"}
            variant={"select"}
            options={[...workstreams]
              .sort((a: any, b: any) =>
                (a.name ?? "").localeCompare(b.name ?? ""),
              )
              .map((r: any) => ({
                value: String(r.id),
                label: r.name,
              }))}
            value={tempFilters.workstream.map(w => w.value)}
            onChange={(_key: string, value: string[]) => {
              const items = workstreams
                .filter((r: any) => value.includes(String(r.id)))
                .map((r: any) => ({ value: String(r.id), label: r.name }));
              handleChange("workstream", items);
            }}
            multiSelect
          />
        )}

        {!hiddenFilters.includes("worktype-group") && (
          <FieldControl
            keyName={"worktype-group"}
            label={"Worktype Group"}
            variant={"select"}
            options={options["worktype-group"]
              ?.slice()
              .sort((a: any, b: any) =>
                a.description.localeCompare(b.description),
              )
              .map((r: any) => ({
                value: String(r.id),
                label: r.description,
              }))}
            value={tempFilters["worktype-group"].map(wt => wt.value)}
            onChange={(_key: string, value: string[]) => {
              const items = options["worktype-group"]
                .filter((r: any) => value.includes(String(r.id)))
                .map((r: any) => ({
                  value: String(r.id),
                  label: r.description,
                }));
              handleChange("worktype-group", items);
            }}
            multiSelect
          />
        )}

        {!hiddenFilters.includes("projectYear") && (
          <FieldControl
            keyName={"projectYear"}
            label={"Reporting Year"}
            variant={"select"}
            options={getYearOptions()}
            value={tempFilters.projectYear.map(p => p.value)}
            onChange={(_key: string, value: string[]) => {
              const items = value.map((v) => ({ value: v, label: v }));
              handleUpdateTempFilters("projectYear", items);
            }}
            multiSelect
          />
        )}

        {!hiddenFilters?.includes("site") && (
          <AsyncMultiAutoComplete
            label="Site"
            value={tempFilters.site ?? []}
            onChange={(val) =>
              handleUpdateTempFilters("site", val as FilterItem[])
            }
            options={sites.map((s: any) => ({
              label: s.description,
              value: String(s.id),
            }))}
            onRegister={(items) => {
              setOptions((prev) => ({
                ...prev,
                siteSelected: items,
              }));
            }}
            onSearch={(text) => setSiteSearch(text)}
            onScrollEnd={() => {
              if (hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
              }
            }}
            loading={isSitesLoading || isFetchingNextPage}
          />
        )}

        {!hiddenFilters?.includes("stakeholder") && (
          <AsyncMultiAutoComplete
            label="Stakeholder"
            value={tempFilters.stakeholder ?? []}
            onChange={(val) =>
              handleUpdateTempFilters("stakeholder", val as FilterItem[])
            }
            options={stakeholders.map((stakeholder: any) => ({
              label: stakeholder.full_name,
              value: String(stakeholder.user_id),
            }))}
            onSearch={(text) => setStakeholderSearch(text)}
            onScrollEnd={() => {
              if (hasStakeholdersNextPage && !isFetchingStakeholdersNextPage) {
                fetchNextStakeholdersPage();
              }
            }}
            loading={isStakeholdersLoading || isFetchingStakeholdersNextPage}
          />
        )}
      </Box>

      <Stack
        direction="row"
        spacing={2}
        justifyContent="flex-end"
        flexShrink={0}
        bgcolor={"action.hover"}
        p={2}
      >
        <Button variant="outlined" onClick={handleResetFilters}>
          Reset
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            handleApplyFilters();
            onClose?.();
          }}
        >
          Apply
        </Button>
      </Stack>
    </Box>
  );
};
